import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  TableColumn,
  StandardizedTable,
  TrendIndicator,
  TableRow,
} from "@/components/ui/standardized-table";
import { useAccount } from "@/contexts/account.context";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import type { TableQueryDTO } from "@/types/table-query";
import { useDrilldownOptional } from "@/features/drilldown";
import { getInternalSearchApiV1InternalSearchCardGet } from "@/backend-sdk/client/sdk.gen";
import type {
  GetInternalSearchApiV1InternalSearchCardGetData,
  InternalSearchCardItem,
} from "@/backend-sdk/client/types.gen";

type InternalSearchQuery =
  GetInternalSearchApiV1InternalSearchCardGetData["query"];
type InternalSearchCrossFilterKey = Extract<
  keyof InternalSearchQuery,
  `filter_${string}`
>;
type InternalSearchCrossFilters = Pick<
  InternalSearchQuery,
  InternalSearchCrossFilterKey
>;
type InternalSearchRow = InternalSearchCardItem & TableRow;

const toTableRows = (items: InternalSearchCardItem[]): InternalSearchRow[] =>
  items.map((item) => ({ ...item }) as InternalSearchRow);

const getInternalSearchCrossFilters = (
  filters: Record<string, string> | undefined,
): Partial<InternalSearchCrossFilters> => {
  if (!filters) return {};

  return {
    filter_date: filters.filter_date,
    filter_device: filters.filter_device,
    filter_region: filters.filter_region,
    filter_city: filters.filter_city,
    filter_category: filters.filter_category,
    filter_product: filters.filter_product,
  };
};

const PesquisaInternaTable: React.FC = () => {
  const { selectedAccount } = useAccount();
  const { filter: globalFilter } = useGlobalFilter();
  const drilldown = useDrilldownOptional();

  // Handler para clique na linha (cross-filtering com Ctrl+click multi-select)
  const handleRowClick = useCallback(
    (row: TableRow, index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;

      const searchTerm = row.search_term as string;
      if (!searchTerm || searchTerm === "N/D") return;

      const isMultiSelect = !!(event?.ctrlKey || event?.metaKey);
      drilldown.selectRow("search_term", searchTerm, searchTerm, isMultiSelect);
    },
    [drilldown],
  );

  // Verifica se uma linha está selecionada
  const isRowSelected = useCallback(
    (row: TableRow, index: number): boolean => {
      if (!drilldown) return false;
      const searchTerm = row.search_term as string;
      return drilldown.isSelected("search_term", searchTerm);
    },
    [drilldown],
  );

  const [rows, setRows] = useState<InternalSearchRow[]>([]);
  const [totalRow, setTotalRow] = useState<InternalSearchRow | null>(null);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const PAGE_SIZE = 10;
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  // Configuração da tabela padronizada
  const columns: TableColumn[] = useMemo(
    () => [
      {
        key: "search_term",
        header: "Termo de pesquisa",
        align: "left",
        render: (value, item, index, isTotal) =>
          isTotal ? value : `${(page - 1) * PAGE_SIZE + index + 1}. ${value}`,
      },
      {
        key: "sessions",
        header: "Sessões",
        type: "number",
      },
      {
        key: "sessions_change_percentage",
        header: "% Δ",
        type: "number",
        render: (value) => {
          const num = typeof value === "number" ? value : Number(value);
          if (!Number.isFinite(num)) return "—";
          return <TrendIndicator value={num} />;
        },
      },
    ],
    [page],
  );

  const fetchData = useCallback(
    async (pg: number) => {
      if (!globalFilter) return;
      try {
        setLoading(true);
        setError(null);
        const startDate = formatDateToISO(globalFilter.dateRange.startDate);
        const endDate = formatDateToISO(globalFilter.dateRange.endDate);
        const comparison = globalFilter.comparison?.type || "previous_period";

        const customerUuid =
          selectedAccount?.customer_uuid ||
          selectedAccount?.id ||
          localStorage.getItem("active_customer_uuid") ||
          undefined;

        const { data: resp } =
          await getInternalSearchApiV1InternalSearchCardGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              page: pg,
              page_size: PAGE_SIZE,
              q: tableQuery.q,
              sort_by: tableQuery.sortBy,
              sort_dir: tableQuery.sortDir,
              filters: tableQuery.filters?.length
                ? JSON.stringify(tableQuery.filters)
                : undefined,
              ...getInternalSearchCrossFilters(drilldown?.apiFilters),
            },
            throwOnError: true,
          });

        setRows(toTableRows(resp.items || []));
        setTotalRow(resp.total_row ? toTableRows([resp.total_row])[0] : null);
        setTotal(resp.pagination?.total || 0);
        setPage(resp.pagination?.page || pg);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Erro ao carregar");
        setRows([]);
        setTotalRow(null);
      } finally {
        setLoading(false);
      }
    },
    [selectedAccount, globalFilter, tableQuery, drilldown],
  );

  // Reset ao mudar filtro global
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(1);
  }, [globalFilter, drilldown?.apiFiltersKey]);

  // Refetch ao mudar página / query / filtro global
  useEffect(() => {
    if (!globalFilter) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchData(page);
  }, [globalFilter, page, tableQuery, fetchData, drilldown?.apiFiltersKey]);

  // A espera fica dentro da tabela: trocar o card inteiro por um esqueleto a cada busca
  // desmontava o campo de pesquisa no meio da digitação e sumia com a paginação.
  return (
    <StandardizedTable
      title="PESQUISA INTERNA"
      description={
        drilldown
          ? "Clique em uma linha para filtrar a página inteira"
          : undefined
      }
      columns={columns}
      data={rows}
      loading={loading}
      total={totalRow || undefined}
      pagination={{
        current: page,
        pageSize: PAGE_SIZE,
        total: total,
        onPageChange: (p) => setPage(p),
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setPage(1);
      }}
      onRowClick={drilldown ? handleRowClick : undefined}
      selectedRowIndex={drilldown ? isRowSelected : undefined}
      clickableRows={!!drilldown}
    />
  );
};

export default PesquisaInternaTable;
